
import { LightningElement, api} from 'lwc';
import ComboboxController from '../../../utils/comboboxController.js';

import { getLabelsByLanguage } from '../../../utils/languageUtil';
import {handleDropdownLengthCl, handleUnifiedClick, suppressEscape} from '../../../utils/comboboxUtils.js';


function handleSldsIsDisabled(cl, disabled=false) {
    if (!cl) return;
    if (disabled) {
        if (cl.includes('slds-is-disabled')) {
            return cl;
        }
        return `${cl} slds-is-disabled`;
    }

    return cl.replace('slds-is-disabled', '');
}

export default class sldsTimePicker extends LightningElement {
    // Dont allow flip
    @api suppressFlip = false;
    @api alwaysFlip = false;
    @api label;
    _isFirst = false;


    _dropdownCl = 'slds-dropdown slds-dropdown_fluid slds-dropdown_length-5';
    @api 
    set dropdownLength(val) {
        this._dropdownLength = val;
        this._dropdownCl = handleDropdownLengthCl(this._options, val);

    }
    get dropdownLength() {
        return this._dropdownLength;
    }
    _inputCl = 'slds-input slds-combobox__input';
    _triggerCl = 'slds-combobox slds-dropdown-trigger slds-dropdown-trigger_click slds-timepicker';
    @api
    set isDisabled(val) {
        this._isDisabled = !!val;
        this._inputCl = handleSldsIsDisabled('slds-input slds-combobox__input', this._isDisabled);
        this._triggerCl = handleSldsIsDisabled(
            'slds-combobox slds-dropdown-trigger slds-dropdown-trigger_click slds-timepicker',
            this._isDisabled
        );
        const uniCl = this._unifiedCl.replace('slds-is-disabled', '');
        this._unifiedCl = `${uniCl} ${this._isDisabled ? 'slds-is-disabled' : ''}`;
    }
    get isDisabled() {
        return this._isDisabled;
    }

    @api
    set options(val) {
        console.log('here is val in set options() of sldsTimePicker.js: ', JSON.stringify(val));
        this._options = Array.isArray(val) ? val : [];
        this._dropdownCl = handleDropdownLengthCl(val, this.dropdownLength);
        if (this._combo) {
            this._combo.options = this._options;
        }
    }
    get options() {
        return this._options;
    }
    _value;
    @api
    set value(val) {
        this._value = val;

        if (this._combo?.isOpen) {
            this._combo.focusFirstOrSelected();
        }
    }
    get value() {
        return this._value;
    }

    @api
    set placeHolder(val) {
        this._placeHolder = getLabelsByLanguage(val);
    }
    get placeHolder() {
        return this._placeHolder ?? getLabelsByLanguage('Select an Option');
    }

    
    // =================== Search-Combobox Filter Handlers =================== \\
    _filteredOptions = [];
    _isFiltering = false;
    filterOptionsOnInput(searchText) {
        const searchLower = (searchText || '').toLowerCase();
        if (!searchLower) {
            this._filteredOptions = [];
            this._isFiltering = false;
            return;
        }
        this._isFiltering = true;
        this._filteredOptions = this.options.filter((option) => {
            return (option.label || '').toLowerCase().includes(searchLower);
        });
    }
    _inputValue = '';
    handleInputChange(event) {
        this._inputValue = event.target.value;
        this.filterOptionsOnInput(event.target.value);
    }
    get templateOptions() {
        const options = this._isFiltering
            ? this._filteredOptions
            : this.options;

        return (options ?? []).map((option, index) => {
            const isSelected = option.value === this._value;

            return {
                ...option,
                keyId: option.keyId ?? option.value ?? String(index),
                domId: option.value,
                isSelected: isSelected,
                ariaSelected: isSelected ? 'true' : 'false',
                ariaChecked: isSelected ? 'true' : 'false',
                className: [
                    'slds-media',
                    'slds-listbox__option',
                    'slds-listbox__option_plain',
                    'slds-media_small',
                    isSelected ? 'slds-is-selected' : ''
                ].filter(Boolean).join(' ')
            };
        });
    }
    // In __this._combo.onSelect()__ change the input value to the selected option label
    changeInputValueOnSelect(value) {
        const prevVal = this._inputValue;
        this._inputValue = this._options.find(opt => opt.value === value)?.label 
            ?? prevVal;
    }
    
    // ======================================================================= \\

    // ================ Additional ComboboxController Handlers =============== \\
    handleInputClick() {
        this._combo.open({ focusSelected: true });
    }
    // ======================================================================= \\


    // ================= Altered ComboboxController Handlers ================= \\
    // On blur, if an option is selected --> clear user-text and set _inputValue to select option label 
    handleBlur() {
        this._combo.handleBlur();
        requestAnimationFrame(() => {
            const selectedLabel = this._options.find(opt => opt.value === this._value)?.label;
            if (selectedLabel) {
                this._inputValue = selectedLabel;
            }
        })
    }

    handleUnifiedClick(event) {
        event.stopPropagation();
        handleUnifiedClick(this, event, false);
    }
    // ======================================================================= \\
    
    
    _combo;
    connectedCallback() {
        this._combo = new ComboboxController({
            host: this,
            isMultiSelect: false,
            isSearchable: false,
            options: this._options,
            getSelectedValue: () => this._value,
            onSelect: ({ value }) => {
                this._value = value;
                this._isFiltering = false;
                this._filteredOptions = [];
                this.changeInputValueOnSelect(value);
                this.dispatchEvent(new CustomEvent('change', {
                    detail: { value }
                }));
            }
        });
    }


    handleInputKeyDown(event) {
        suppressEscape(event);
        this._combo.handleInputKeyDown(event);
    }
    handleTriggerClick() {
        return;
        this._combo.handleTriggerClick();
    }
    deflectFocus(event) {
        this._combo.deflectFocus(event);
    }
    
    handleFocus() {
        this._combo.handleFocus();
        if (this._unifiedCl && !this._unifiedCl.includes('slds-is-disabled')) {
            setTimeout(() => {
                if (!this._combo.isOpen) {
                    this._combo.open({ focusSelected: true });
                }
            }, 50);
        }
    }

    // Values added in these 2 function
    handleOptionClick(event) {
        this._combo.handleOptionClick(event);
    }
    handleTriggerKeyDown(event) {
        suppressEscape(event);
        this._combo.handleTriggerKeyDown(event);
    }

    

    get displayValue() {
        const match = this._options.find((opt) => opt.value === this._value);
        return match?.label || '';
    }
    get hasOptions() {
        return this.options.length > 0;
    }


    _unifiedCl = '';
    @api 
    set unified(val) {
        this._unified = val;
        if (val) {
            this._applyVarsDirect('--input-border', 'none');
            this._unifiedCl = `${this._unifiedCl} gw-unified-form-element`.trim();
        }
    }
    get unified() {
        return this._unified;
    }
    @api 
    set fontSize(val) {
        this._fontSize = val;
        if (val) {
            this._applyVarsDirect('--input-font-size', val);
        }
    }
    get fontSize() {
        return this._fontSize;
    }
    @api 
    set inputHeight(val) {
        this._inputHeight = val;
        if (val) {
            this._applyVarsDirect('--input-height', val)
        }
    }
    get inputHeight() {
        return this._inputHeight;
    }
    @api 
    set borderRadius(val) {
        this._borderRadius = val;
        if (val) {
            this._applyVarsDirect('--slds-c-input-radius-border', val);
        }
    }
    get borderRadius() {
        return this._borderRadius;
    }

    // Apply Styles to the host 👉 Can pass 1.Array of Arrays   2.Array of Object   3.Strings
    _applyVarsDirect(cssVar, varStyle=null) {

        // Check that CSS var starts with '--' and has a non-null stlye 
        const isValid = (cssVarP, varStyleP) => {
            return typeof cssVarP === 'string' && 
                typeof varStyleP === 'string' &&
                cssVarP?.startsWith('--') && 
                varStyleP;
        }



        const el = this.template.host;
        
        if (Array.isArray(cssVar)) {
            // Handle Array of vars
            cssVar.forEach(hostVar => {
                if (Array.isArray(hostVar) && isValid(hostVar[0], hostVar[1])) {
                    // Handle Array structure  (CSS var at index 0)
                    el.style.setProperty(hostVar[0], hostVar[1]);
                } else if (typeof hostVar === 'object' && isValid(hostVar.cssVar, hostVar.varStyle)) {
                    // Handle Object structure (cssVar & varStyle keys)
                    el.style.setProperty(hostVar.cssVar, hostVar.varStyle)
                }
            });
        } else if (isValid(cssVar, varStyle)) {
            // Handle string arguments
            el.style.setProperty(cssVar,  varStyle);
        }
    }
}